export type PlanId = "standard" | "pro" | "unlimited";

export type QuestionOptionTone = "blue" | "green" | "amber" | "violet";

export type Question = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  options: Array<{
    id: string;
    label: string;
    description: string;
    icon: "moon" | "battery" | "terminal" | "clock" | "shield" | "desktop";
    tone: QuestionOptionTone;
  }>;
};

export type Answers = Record<string, string>;

export const questions: Question[] = [
  {
    id: "session_break",
    eyebrow: "Step 1 of 4",
    title: "What breaks your agent sessions most often?",
    subtitle: "Tell us the moment long-running work dies on your Mac.",
    options: [
      {
        id: "display_sleep",
        label: "Display sleep stalls the run",
        description: "The screen sleeps and you lose visibility or momentum.",
        icon: "moon",
        tone: "green",
      },
      {
        id: "system_sleep",
        label: "Mac sleeps mid-task",
        description: "The machine sleeps before the agent finishes, lid closed or idle.",
        icon: "terminal",
        tone: "blue",
      },
      {
        id: "battery_drain",
        label: "Battery drain from staying awake",
        description: "You keep it awake manually and wake up to a dead laptop.",
        icon: "battery",
        tone: "amber",
      },
      {
        id: "stepping_away",
        label: "Stepping away or relocating",
        description: "Café to home or desk to meeting, you leave before the run finishes.",
        icon: "clock",
        tone: "violet",
      },
    ],
  },
  {
    id: "run_frequency",
    eyebrow: "Step 2 of 4",
    title: "How often do you kick off long agent runs?",
    subtitle: "Roughly how much of your week leans on unattended sessions.",
    options: [
      {
        id: "multiple_daily",
        label: "Several times a day",
        description: "Agents are core to how you work, runs back to back.",
        icon: "terminal",
        tone: "violet",
      },
      {
        id: "daily",
        label: "About once a day",
        description: "A long run most working days.",
        icon: "clock",
        tone: "blue",
      },
      {
        id: "few_week",
        label: "A few times a week",
        description: "Bigger jobs you set off when they come up.",
        icon: "moon",
        tone: "green",
      },
      {
        id: "occasional",
        label: "Now and then",
        description: "Occasional long builds or scripts.",
        icon: "battery",
        tone: "amber",
      },
    ],
  },
  {
    id: "run_length",
    eyebrow: "Step 3 of 4",
    title: "How long does a typical run go?",
    subtitle: "From kickoff to the moment it's safe to walk away.",
    options: [
      {
        id: "under_30",
        label: "Under 30 minutes",
        description: "Short tasks, but enough to step away from.",
        icon: "clock",
        tone: "blue",
      },
      {
        id: "half_to_90",
        label: "30 to 90 minutes",
        description: "Long enough that the screen sleeps mid-run.",
        icon: "moon",
        tone: "green",
      },
      {
        id: "two_to_four",
        label: "A few hours",
        description: "Deep builds, test suites, multi-step agent work.",
        icon: "terminal",
        tone: "violet",
      },
      {
        id: "overnight",
        label: "Overnight / all day",
        description: "You leave it running and check back later.",
        icon: "battery",
        tone: "amber",
      },
    ],
  },
  {
    id: "mac_setup",
    eyebrow: "Step 4 of 4",
    title: "Which setup sounds closest?",
    subtitle: "Last one. We'll match you to the right license.",
    options: [
      {
        id: "one_mac",
        label: "One main Mac",
        description: "Personal workflow, simple setup, one device.",
        icon: "desktop",
        tone: "blue",
      },
      {
        id: "multi_mac",
        label: "Up to five Macs",
        description: "Work machine, home machine, or a multi-device setup.",
        icon: "shield",
        tone: "green",
      },
      {
        id: "team_research",
        label: "Testing for a team",
        description: "Start with the best value license and expand later.",
        icon: "clock",
        tone: "violet",
      },
    ],
  },
];

export const getRecommendedPlan = (answers: Answers): Exclude<PlanId, "unlimited"> =>
  answers.mac_setup === "one_mac" ? "standard" : "pro";

const findAnswerLabel = (questionId: string, optionId: string | undefined): string | null => {
  if (!optionId) return null;
  const question = questions.find((item) => item.id === questionId);
  return question?.options.find((option) => option.id === optionId)?.label ?? null;
};

// Honest, transparent estimate of weekly agent runtime exposed to interruption.
// Derived purely from the user's own answers. No randomness, no inflation.
const RUNS_PER_WEEK: Record<string, number> = {
  multiple_daily: 14,
  daily: 6,
  few_week: 3,
  occasional: 1,
};

const HOURS_PER_RUN: Record<string, number> = {
  under_30: 0.5,
  half_to_90: 1.25,
  two_to_four: 3,
  overnight: 8,
};

const RUN_LENGTH_SHORT: Record<string, string> = {
  under_30: "Under 30 min",
  half_to_90: "30 to 90 min",
  two_to_four: "A few hours",
  overnight: "Overnight",
};

// Short noun phrase for the metric sentence ("...exposed to display sleep").
const RISK_SHORT: Record<string, string> = {
  display_sleep: "display sleep",
  system_sleep: "your Mac sleeping mid-task",
  battery_drain: "battery drain",
  stepping_away: "stepping away",
};

type IconName = Question["options"][number]["icon"];

const RISK_ICON: Record<string, IconName> = {
  display_sleep: "moon",
  system_sleep: "terminal",
  battery_drain: "battery",
  stepping_away: "clock",
};

export const getResultInsights = (answers: Answers) => {
  const sessionBreak = findAnswerLabel("session_break", answers.session_break) ?? "Interrupted sessions";
  const macSetup = findAnswerLabel("mac_setup", answers.mac_setup) ?? "Multi-Mac setup";
  const recommendedPlan = getRecommendedPlan(answers);
  const planName = recommendedPlan === "standard" ? "Standard" : "Pro";
  const planDetail = recommendedPlan === "standard" ? "1 Mac" : "up to 5 Macs";

  const runsPerWeek = RUNS_PER_WEEK[answers.run_frequency] ?? 3;
  const hoursPerRun = HOURS_PER_RUN[answers.run_length] ?? 1.25;
  // Clamp to a believable range so edge combinations stay grounded.
  const exposedHoursPerWeek = Math.min(60, Math.max(1, Math.round(runsPerWeek * hoursPerRun)));
  const typicalRun = RUN_LENGTH_SHORT[answers.run_length] ?? "Long runs";
  const riskShort = RISK_SHORT[answers.session_break] ?? "interrupted sessions";
  const riskIcon = RISK_ICON[answers.session_break] ?? "moon";
  const exposureCaption = `Based on ~${runsPerWeek} run${runsPerWeek === 1 ? "" : "s"}/week at ~${hoursPerRun}h each. Your estimate.`;

  return {
    sessionBreak,
    macSetup,
    typicalRun,
    riskShort,
    riskIcon,
    recommendedPlan,
    planName,
    planDetail,
    exposedHoursPerWeek,
    exposureCaption,
    headline:
      answers.session_break === "battery_drain"
        ? "Awakeo keeps agents running without draining your battery."
        : answers.session_break === "system_sleep"
          ? "Awakeo keeps sessions alive when your Mac would normally sleep."
          : answers.session_break === "stepping_away"
            ? "Awakeo keeps sessions running while you step away or change locations."
            : "Awakeo keeps sessions alive while your display can sleep.",
    summary: `Your biggest friction is ${sessionBreak.toLowerCase()}. For a ${macSetup.toLowerCase()} setup, ${planName} is the best fit.`,
  };
};

export const MONEY_BACK_GUARANTEE_DAYS = 15;
export const moneyBackGuaranteeLabel = `${MONEY_BACK_GUARANTEE_DAYS}-day money-back guarantee`;

export const plans = [
  {
    id: "standard" as const,
    name: "Standard",
    originalPrice: "$29",
    price: "$19",
    detail: "1 device",
    description: "One Mac, full Awakeo feature set.",
    cta: "Get Standard",
    features: ["One-time purchase", "1 device", "Lifetime updates", "Menu bar + shortcut"],
  },
  {
    id: "pro" as const,
    name: "Pro",
    originalPrice: "$39",
    price: "$29",
    detail: "5 devices",
    description: "Best for multi-Mac developers.",
    cta: "Get Pro",
    badge: "Popular",
    badgeVariant: "primary" as const,
    features: ["One-time purchase", "Up to 5 devices", "Lifetime updates", "Email support"],
  },
];

export const checkoutPlans = [...plans];

export const valueChips = ["Local-first", "Native macOS", "One-time payment", "Battery-aware"] as const;

export const resultBullets = [
  "Agent sessions survive display sleep and short breaks",
  "Battery threshold stops awake mode before drain",
  "Menu bar toggle + global shortcut",
  "One-time purchase, no subscription",
] as const;

// "After payment" reassurance timeline shown on checkout.
export const checkoutAfterPaymentSteps = [
  {
    title: "Download instantly after checkout",
    subtitle: "The link is also emailed to you.",
  },
  {
    title: "Install and activate with your email",
    subtitle: "Your purchase email unlocks the app.",
  },
  {
    title: "Launch Awakeo from the menu bar",
    subtitle: "Keep your Mac awake on demand or until your run finishes.",
  },
] as const;

export const paymentFaqs = [
  { question: "Is this a subscription?", answer: "No. Awakeo is a one-time purchase with lifetime updates." },
  {
    question: "What does Awakeo do?",
    answer: "It keeps your Mac awake for long-running agent sessions, builds, and scripts while you step away, with display-sleep and battery-aware options.",
  },
  {
    question: "What happens if I lock my Mac or close the lid?",
    answer:
      "Awakeo gives you three display modes: let the display sleep (saves battery while the run continues), sleep and lock your Mac when you step away, or keep the display on to watch it work. For lid-closed sessions, enable lid-closed mode in Awakeo's settings once — it keeps your Mac awake with the lid shut so agents keep running while you relocate.",
  },
  {
    question: "Is my data sent to the cloud?",
    answer: "Awakeo is local-first. Session content stays on your Mac; activation uses our license service only.",
  },
  { question: "Refund policy?", answer: "15-day money-back guarantee. Email support@awakeo.app." },
] as const;

export const socialProof = {
  rating: "4.9",
  ratingMax: "5",
  audience: "2,000+ users",
  avatars: [
    "/assets/reviews/man-1.webp",
    "/assets/reviews/girl-1.webp",
    "/assets/reviews/darshan-patel-headshot-96w.webp",
  ],
} as const;

export const testimonials = [
  {
    quote: "I kick off Codex runs and actually walk away. Awakeo is the missing menu bar piece.",
    author: "Theo",
    detail: "Staff engineer",
    image: "/assets/reviews/man-1.webp",
  },
  {
    quote: "Display sleeps, build keeps going. Exactly what I wanted without a loud utility.",
    author: "Lena",
    detail: "Indie hacker",
    image: "/assets/reviews/girl-1.webp",
  },
  {
    quote: "Battery threshold saved my laptop twice during long agent sessions at a café.",
    author: "Arjun",
    detail: "DevOps consultant",
    image: "/assets/reviews/darshan-patel-headshot-96w.webp",
  },
  {
    quote: "No subscription, runs quietly in the menu bar, and my overnight builds finally finish. Easy buy.",
    author: "Sofia",
    detail: "Backend engineer",
    image: "/assets/reviews/girl-2.webp",
  },
] as const;

export type PaymentExplainerStep = {
  title: string;
  description: string;
  icon: Question["options"][number]["icon"];
  tone: QuestionOptionTone;
  bonus?: boolean;
};

// "How Awakeo works" — the menu bar flow shown as an expandable on checkout.
export const paymentExplainerSteps: PaymentExplainerStep[] = [
  {
    title: "Launch from the menu bar",
    description: "One click from the macOS menu bar — no dock clutter, no setup ritual.",
    icon: "desktop",
    tone: "blue",
  },
  {
    title: "Choose how long to stay awake",
    description: "Timed, indefinite, or until your current run finishes. Awakeo handles the rest.",
    icon: "clock",
    tone: "violet",
  },
  {
    title: "Your display can still sleep",
    description: "The screen dims and sleeps to save power while the run keeps going underneath.",
    icon: "moon",
    tone: "green",
  },
  {
    title: "Battery-aware guardrails",
    description: "Set a battery threshold and Awakeo drops awake mode before your Mac drains.",
    icon: "battery",
    tone: "amber",
  },
  {
    title: "Automatic agent continuity",
    description:
      "The key feature: Awakeo detects active agent sessions — Claude Code, Codex, Cursor — and keeps them alive on its own while you step away.",
    icon: "terminal",
    tone: "violet",
    bonus: true,
  },
];
